import { defineStore } from "pinia";
import { Cart, CartRow, CartSummary } from "@/models/Cart";
import { BasketApi } from "@/services/Api";

const getBasketFromDom = (): Cart | null => {
  const basketElement = document.querySelector("[data-basket]");
  if (basketElement) {
    const basketDataAttribute = basketElement.getAttribute("data-basket");
    if (basketDataAttribute) {
      return JSON.parse(basketDataAttribute) as Cart;
    }
  }
  return null;
};

export const useCart = defineStore("cart", {
  state: (): Cart => {
    const rows: CartRow[] = [];
    const summary: CartSummary = {
      fees: {
        amount: "",
        amountIncVat: "",
        vat: "",
      },
      freight: {
        amount: "",
        amountIncVat: "",
        vat: "",
      },
      items: {
        amount: "",
        amountIncVat: "",
        vat: "",
      },
      total: {
        amount: "",
        amountIncVat: "",
        vat: "",
        amountIncVatWithoutFreight: "",
        amountWithoutFreight: "",
      },
      totalDiscounts: {
        amount: "",
        amountIncVat: "",
        vat: "",
      },
    };
    const cart: Cart = {
      id: "",
      discountCode: "",
      rows: rows,
      summary: summary,
      hasRemovedItems: false,
      hasSpecialFreight: false,
      hasSelectedDeliveryMethod: false,
      loading: false,
      reachedMinimumBuyableAmount: false,
      minimumBuyableAmountMessage: "",
      isCartModalOpen: false
    };
    return cart;
  },
  getters: {
    itemQuantity: (state) => {
      return state.rows.reduce((sum, item) => sum + item.quantity, 0);
    },
  },
  actions: {
    async load(): Promise<void> {
      let basket = getBasketFromDom();
      if (!basket) {
        basket = await BasketApi.getBasket();
      }
      this.$patch({ ...basket });
    },
    async set(basket: Cart): Promise<void> {
      this.$patch({ ...basket });
    },
    async setDiscountCode(discountCode: string): Promise<void> {
      this.setLoadingState(true);
      const basket = await BasketApi.setDiscountCode(discountCode);
      this.$patch({ ...basket });
      this.setLoadingState(false);
    },
    async addToCart(partNo: string, quantity: number, incrementExisting: boolean): Promise<void> {
      this.setLoadingState(true);
      const basket = await BasketApi.addToBasket(partNo, quantity, incrementExisting);
      this.$patch({ ...basket });
      this.setLoadingState(false);
    },
    async removeFromCart(partNo: string): Promise<void> {
      this.setLoadingState(true);
      const basket = await BasketApi.removeFromBasket(partNo);
      this.$patch({ ...basket });
      this.setLoadingState(false);
    },
    async removeRangeFromCart(partNo: string[]): Promise<void> {
      this.setLoadingState(true);
      const basket = await BasketApi.removeRangeFromBasket(partNo);
      this.$patch({ ...basket });
      this.setLoadingState(false);
    },
    async updateQuantityFromCart(partNo: string, quantity: number): Promise<void> {
      this.setLoadingState(true);
      const basket = await BasketApi.updateBasket(partNo, quantity);
      this.$patch({ ...basket });
      this.setLoadingState(false);
    },
    setLoadingState(state: boolean): void {
      this.$patch({ loading: state });
    },
  },
});
